import React, { useEffect, useState } from 'react';
import { ArrowLeft, Ban, CheckCircle, KeyRound, Lock, Wallet, Snowflake, ShoppingBag, Crown, TrendingUp, TrendingDown, X, Trash2, FileX, UserPlus, Users, Dices } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { adminService } from '../../services/adminService';
import { referralAdminService } from '../../services/referralAdminService';
import Toast from '../../components/Toast';
import { useTranslation } from '../../i18n/LocaleContext';

const HistoryTable = ({ title, rows, columns, emptyText }) => (
  <section className="overflow-hidden rounded-xl border border-gray-700 bg-gray-800">
    <h2 className="border-b border-gray-700 px-5 py-4 text-lg font-bold text-white">{title}</h2>
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-gray-900 text-left text-xs uppercase text-gray-400">
          <tr>{columns.map(([label]) => <th key={label} className="whitespace-nowrap px-5 py-3">{label}</th>)}</tr>
        </thead>
        <tbody className="divide-y divide-gray-700">
          {rows.length === 0 ? (
            <tr><td colSpan={columns.length} className="px-5 py-6 text-center text-gray-400">{emptyText}</td></tr>
          ) : rows.map((row) => (
            <tr key={row.id} className="text-gray-300 hover:bg-gray-700">
              {columns.map(([label, render]) => <td key={label} className="whitespace-nowrap px-5 py-3">{render(row)}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </section>
);

const AdminUserDetail = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [histories, setHistories] = useState({ deposits: [], withdraws: [], orders: [] });
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  // Check user type
  const userStr = localStorage.getItem('user');
  const currentUser = userStr ? JSON.parse(userStr) : null;
  const isReferralAdmin = currentUser?.userType === 'referral_admin';
  const [balanceAction, setBalanceAction] = useState(null);
  const [balanceAmount, setBalanceAmount] = useState('');
  const [vipLevels, setVipLevels] = useState([]);
  const [selectedVip, setSelectedVip] = useState('');
  const [toast, setToast] = useState(null);
  const [showReferrerModal, setShowReferrerModal] = useState(false);
  const [referrerSearch, setReferrerSearch] = useState('');
  const [referrerResults, setReferrerResults] = useState([]);
  const [showReferralCodeModal, setShowReferralCodeModal] = useState(false);

  useEffect(() => {
    fetchUser();
  }, [id]);

  const fetchUser = async () => {
    try {
      setLoading(true);
      const service = isReferralAdmin ? referralAdminService : adminService;

      // Fetch user detail
      const data = await service.getUserDetail(id);
      setUser(data.user);
      setSelectedVip(data.user.vip || '');
      setHistories(data.histories || { deposits: [], withdraws: [], orders: [] });

      // Only fetch VIP configs for super admin
      if (!isReferralAdmin) {
        try {
          const vipData = await adminService.getVipConfigs();
          const levels = vipData.vip_levels || [];
          // Add VIP 0 at the beginning if not already present
          if (!levels.find(v => v.level === 'VIP 0')) {
            levels.unshift({ id: 0, level: 'VIP 0', order_count: 0, commission_rate: 0 });
          }
          setVipLevels(levels);
        } catch (error) {
          console.error('VIP config error:', error);
        }
      } else {
        // For referral admin, create basic VIP levels
        setVipLevels([
          { id: 0, level: 'VIP 0' },
          { id: 1, level: 'VIP 1' },
          { id: 2, level: 'VIP 2' },
          { id: 3, level: 'VIP 3' },
          { id: 4, level: 'VIP 4' },
          { id: 5, level: 'VIP 5' },
          { id: 6, level: 'VIP 6' },
          { id: 7, level: 'VIP 7' },
          { id: 8, level: 'VIP 8' },
          { id: 9, level: 'VIP 9' }
        ]);
      }
    } catch (error) {
      alert(error.message);
      navigate('/admin/users');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => new Intl.NumberFormat('vi-VN', {
    style: 'currency',
    currency: 'USD'
  }).format(amount || 0);

  const handlePasswordChange = async () => {
    const newPassword = window.prompt(t('aUdPromptNewPassword'));
    if (!newPassword) return;

    try {
      setProcessing(true);
      await adminService.changeUserPassword(id, newPassword);
      alert(t('aUdPasswordChanged'));
    } catch (error) {
      alert(error.message);
    } finally {
      setProcessing(false);
    }
  };

  const openBalanceModal = (action) => {
    setBalanceAction(action);
    setBalanceAmount('');
  };

  const closeBalanceModal = () => {
    if (!processing) {
      setBalanceAction(null);
      setBalanceAmount('');
    }
  };

  const handleAdjustBalance = async (event) => {
    event.preventDefault();
    const amount = Number(balanceAmount.replace(/[^0-9.]/g, ''));

    if (!Number.isFinite(amount) || amount <= 0) {
      setToast({ type: 'error', message: t('invalidAmount') });
      return;
    }

    try {
      setProcessing(true);
      const service = isReferralAdmin ? referralAdminService : adminService;
      await service.adjustBalance(id, amount, balanceAction);
      setBalanceAction(null);
      setBalanceAmount('');
      await fetchUser();
      setToast({
        type: 'success',
        message: balanceAction === 'add' ? t('balanceAdded') : t('balanceDeducted')
      });
    } catch (error) {
      setToast({ type: 'error', message: error.message });
    } finally {
      setProcessing(false);
    }
  };

  const handleUnlockFrozenBalance = async () => {
    const frozenBalance = Number(user.balance_lock);
    if (frozenBalance <= 0) return;

    if (!window.confirm(t('aUdUnlockFrozenConfirm', { amount: formatCurrency(frozenBalance) }))) return;

    try {
      setProcessing(true);
      const result = await adminService.unlockUserBalance(id);
      alert(t('aUdUnlockFrozenDone', { amount: formatCurrency(result.unlocked_amount) }));
      await fetchUser();
    } catch (error) {
      alert(error.message);
    } finally {
      setProcessing(false);
    }
  };

  const handleSetVip = async () => {
    if (!selectedVip || selectedVip === user.vip) return;
    try {
      setProcessing(true);
      const service = isReferralAdmin ? referralAdminService : adminService;
      await service.setUserVip(id, selectedVip);
      await fetchUser();
      setToast({ type: 'success', message: t('vipSaved') });
    } catch (error) {
      setToast({ type: 'error', message: error.message });
    } finally {
      setProcessing(false);
    }
  };

  const handleLock = async () => {
    const nextStatus = Number(user.active) === 1 ? 'locked' : 'active';
    const message = nextStatus === 'locked' ? t('aUdLockConfirm') : t('aUdUnlockConfirm');
    if (!window.confirm(message)) return;

    try {
      setProcessing(true);
      const service = isReferralAdmin ? referralAdminService : adminService;
      await service.toggleUserLock(id);
      await fetchUser();
    } catch (error) {
      alert(error.message);
    } finally {
      setProcessing(false);
    }
  };

  const handleDeleteOrderHistory = async () => {
    if (!window.confirm(t('aUdDeleteHistoryConfirm'))) return;

    try {
      setProcessing(true);
      await adminService.deleteUserOrderHistory(id);
      setToast({ type: 'success', message: t('aUdDeleteHistoryDone') });
      await fetchUser();
    } catch (error) {
      setToast({ type: 'error', message: error.message });
    } finally {
      setProcessing(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!window.confirm(t('aUdDeleteUserConfirm'))) return;

    const confirmText = window.prompt(t('aUdDeleteUserPrompt', { keyword: 'XOA USER' }));
    if (confirmText !== 'XOA USER') {
      alert(t('aUdCancelled'));
      return;
    }

    try {
      setProcessing(true);
      await adminService.deleteUser(id);
      setToast({ type: 'success', message: t('aUdDeleteUserDone') });
      setTimeout(() => navigate('/admin/users'), 2000);
    } catch (error) {
      setToast({ type: 'error', message: error.message });
      setProcessing(false);
    }
  };

  const handleSearchReferrer = async (searchTerm) => {
    try {
      const response = await adminService.searchReferrer(searchTerm, id);
      setReferrerResults(response.users || []);
    } catch (error) {
      console.error('Search referrer error:', error);
    }
  };

  const handleSetReferrer = async (referrerId) => {
    try {
      setProcessing(true);
      await adminService.setReferrer(id, referrerId);
      setToast({ type: 'success', message: t('aUdReferrerSet') });
      setShowReferrerModal(false);
      fetchUser();
    } catch (error) {
      setToast({ type: 'error', message: error.message });
    } finally {
      setProcessing(false);
    }
  };

  const handleRemoveReferrer = async () => {
    if (!window.confirm(t('aUdRemoveReferrerConfirm'))) return;
    try {
      setProcessing(true);
      await adminService.removeReferrer(id);
      setToast({ type: 'success', message: t('aUdReferrerRemoved') });
      fetchUser();
    } catch (error) {
      setToast({ type: 'error', message: error.message });
    } finally {
      setProcessing(false);
    }
  };

  const handleGenerateReferralCode = async () => {
    try {
      setProcessing(true);
      const response = await adminService.generateReferralCode(id);
      setToast({ type: 'success', message: response.message });
      setShowReferralCodeModal(false);
      fetchUser();
    } catch (error) {
      setToast({ type: 'error', message: error.message });
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return <div className="flex h-64 items-center justify-center text-gray-400">{t('aUdLoading')}</div>;
  }

  if (!user) return null;

  const isActive = Number(user.active) === 1;
  const cards = [
    { label: t('aUdCardBalance'), value: formatCurrency(user.balance), icon: Wallet },
    { label: t('aUdCardFrozen'), value: formatCurrency(user.balance_lock), icon: Snowflake, action: Number(user.balance_lock) > 0 ? handleUnlockFrozenBalance : null },
    { label: t('aUdCardCompleted'), value: user.completed_orders || 0, icon: ShoppingBag },
    { label: t('aUdCardRemaining'), value: Math.max(user.remaining_orders || 0, 0), icon: ShoppingBag },
    { label: t('aUdCardTotalDeposit'), value: formatCurrency(user.total_deposit), icon: TrendingUp },
    { label: t('aUdCardTotalWithdraw'), value: formatCurrency(user.total_withdraw), icon: TrendingDown }
  ];

  return (
    <div>
      {toast && <Toast type={toast.type} message={toast.message} onClose={() => setToast(null)} />}
      <button onClick={() => navigate('/admin/users')} className="mb-6 flex items-center gap-2 text-gray-300 hover:text-white">
        <ArrowLeft className="h-5 w-5" /> {t('aUdBack')}
      </button>

      <div className="mb-6 space-y-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white">{t('aUdTitle')}</h1>
          <p className="mt-1 text-sm sm:text-base text-gray-400">ID #{user.id}</p>
        </div>

        {/* VIP Selection */}
        <div className="flex items-center gap-2 rounded-lg bg-gray-800 px-3 py-2 w-fit">
          <Crown className="h-4 w-4 text-yellow-400 flex-shrink-0" />
          <select value={selectedVip} onChange={(event) => setSelectedVip(event.target.value)} disabled={processing} className="bg-transparent text-sm font-medium text-white outline-none">
            {vipLevels.map((vip) => <option key={vip.id} value={vip.level} className="bg-gray-800">{vip.level}</option>)}
          </select>
          <button onClick={handleSetVip} disabled={processing || selectedVip === user.vip} className="rounded bg-brand-500 px-2 py-1 text-xs font-semibold text-white disabled:opacity-50">{t('aUdSaveVip')}</button>
        </div>

        {/* Action Buttons */}
        <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2 sm:gap-3">
          <button onClick={() => openBalanceModal('add')} disabled={processing} className="rounded-lg bg-green-600 px-3 sm:px-4 py-2 text-sm sm:text-base font-medium text-white hover:bg-green-700 disabled:opacity-50">
            {t('aUdAddMoney')}
          </button>
          <button onClick={() => openBalanceModal('subtract')} disabled={processing} className="rounded-lg bg-gray-700 px-3 sm:px-4 py-2 text-sm sm:text-base font-medium text-white hover:bg-gray-600 disabled:opacity-50">
            {t('aUdSubtractMoney')}
          </button>
          <button onClick={handlePasswordChange} disabled={processing} className="flex items-center justify-center gap-2 rounded-lg bg-gray-700 px-3 sm:px-4 py-2 text-sm sm:text-base font-medium text-white hover:bg-gray-600 disabled:opacity-50">
            <KeyRound className="h-4 w-4" /> <span className="hidden sm:inline">{t('aUdChangePassword')}</span><span className="sm:hidden">{t('aUdChangePasswordShort')}</span>
          </button>
          <button onClick={handleLock} disabled={processing} className={`flex items-center justify-center gap-2 rounded-lg px-3 sm:px-4 py-2 text-sm sm:text-base font-medium text-white disabled:opacity-50 ${isActive ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'}`}>
            {isActive ? <><Lock className="h-4 w-4" /> <span className="hidden sm:inline">{t('aUdLockUser')}</span><span className="sm:hidden">{t('aUdLockShort')}</span></> : <><CheckCircle className="h-4 w-4" /> <span className="hidden sm:inline">{t('aUdUnlock')}</span><span className="sm:hidden">{t('aUdUnlockShort')}</span></>}
          </button>
          <button onClick={handleDeleteOrderHistory} disabled={processing} className="flex items-center justify-center gap-2 rounded-lg bg-gray-700 px-3 sm:px-4 py-2 text-sm sm:text-base font-medium text-white hover:bg-gray-600 disabled:opacity-50">
            <FileX className="h-4 w-4" /> <span className="hidden sm:inline">{t('aUdDeleteOrderHistoryBtn')}</span><span className="sm:hidden">{t('aUdDeleteHistoryShort')}</span>
          </button>
          <button onClick={() => setShowReferrerModal(true)} disabled={processing} className="flex items-center justify-center gap-2 rounded-lg bg-gray-700 px-3 sm:px-4 py-2 text-sm sm:text-base font-medium text-white hover:bg-gray-600 disabled:opacity-50">
            <Users className="h-4 w-4" /> <span className="hidden sm:inline">{t('aUdSetReferrer')}</span><span className="sm:hidden">{t('aUdSetReferrerShort')}</span>
          </button>
          {!user.referral_code && (
            <button onClick={() => setShowReferralCodeModal(true)} disabled={processing} className="flex items-center justify-center gap-2 rounded-lg bg-brand-500 px-3 sm:px-4 py-2 text-sm sm:text-base font-medium text-white hover:bg-brand-600 disabled:opacity-50">
              <Dices className="h-4 w-4" /> <span className="hidden sm:inline">{t('aUdGenerateReferralCode')}</span><span className="sm:hidden">{t('aUdGenerateCodeShort')}</span>
            </button>
          )}
          <button onClick={handleDeleteUser} disabled={processing} className="flex items-center justify-center gap-2 rounded-lg bg-red-700 px-3 sm:px-4 py-2 text-sm sm:text-base font-medium text-white hover:bg-red-800 disabled:opacity-50">
            <Trash2 className="h-4 w-4" /> <span className="hidden sm:inline">{t('aUdDeleteUserBtn')}</span><span className="sm:hidden">{t('aUdDeleteShort')}</span>
          </button>
        </div>

        {/* Referrer Info */}
        <div className="flex flex-wrap gap-2">
          {user.referrer_id && (
            <div className="flex items-center gap-2 rounded-lg bg-brand-800/30 border border-brand-700 px-4 py-2">
              <Users className="h-4 w-4 text-gray-400" />
              <span className="text-sm text-gray-300">{t('aUdReferrerLabel')} <span className="font-semibold text-white">{user.referrer_username || `ID #${user.referrer_id}`}</span></span>
              <button onClick={handleRemoveReferrer} disabled={processing} className="ml-2 text-red-400 hover:text-red-300">
                <X className="h-4 w-4" />
              </button>
            </div>
          )}
          {user.referral_code && (
            <div className="flex items-center gap-2 rounded-lg bg-yellow-900/30 border border-yellow-700 px-4 py-2">
              <span className="text-sm text-gray-300">{t('aUdReferralCodeLabel')} <code className="font-mono font-semibold text-white">{user.referral_code}</code></span>
            </div>
          )}
        </div>
      </div>

      {balanceAction && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
          <form onSubmit={handleAdjustBalance} className="w-full max-w-md rounded-2xl border border-gray-700 bg-gray-800 p-6 shadow-2xl">
            <div className="mb-5 flex items-start justify-between">
              <div>
                <h2 className="text-xl font-bold text-white">{balanceAction === 'add' ? t('aUdModalAddTitle') : t('aUdModalSubtractTitle')}</h2>
                <p className="mt-1 text-sm text-gray-400">{t('aUdCurrentBalance', { amount: formatCurrency(user.balance) })}</p>
              </div>
              <button type="button" onClick={closeBalanceModal} disabled={processing} className="rounded-lg p-1 text-gray-400 hover:bg-gray-700 hover:text-white"><X className="h-5 w-5" /></button>
            </div>
            <label className="mb-2 block text-sm font-medium text-gray-300">{balanceAction === 'add' ? t('aUdAmountAdd') : t('aUdAmountSubtract')}</label>
            <input
              autoFocus
              inputMode="numeric"
              value={balanceAmount}
              onChange={(event) => setBalanceAmount(event.target.value.replace(/[^0-9]/g, ''))}
              placeholder={t('aUdAmountPlaceholder')}
              className="w-full rounded-lg border border-gray-600 bg-gray-900 px-4 py-3 text-lg font-semibold text-white outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
            />
            {balanceAmount && <p className="mt-2 text-sm text-white">{formatCurrency(balanceAmount)}</p>}
            <div className="mt-6 flex justify-end gap-3">
              <button type="button" onClick={closeBalanceModal} disabled={processing} className="rounded-lg bg-gray-700 px-4 py-2 font-medium text-white hover:bg-gray-600 disabled:opacity-50">{t('aUdCancel')}</button>
              <button type="submit" disabled={processing || !balanceAmount} className="rounded-lg bg-brand-500 px-4 py-2 font-medium text-white hover:bg-brand-600 disabled:opacity-50">
                {processing ? t('aUdProcessing') : balanceAction === 'add' ? t('aUdConfirmAdd') : t('aUdConfirmSubtract')}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="mb-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
        <div className="rounded-xl border border-gray-700 bg-gray-800 p-4 sm:p-5">
          <p className="text-xs sm:text-sm text-gray-400">{t('aUdLabelUsername')}</p>
          <p className="mt-1 text-lg sm:text-xl font-bold text-white truncate">{user.username}</p>
          <p className="mt-2 text-xs sm:text-sm text-gray-300 truncate">{t('aUdPhone', { value: user.phone || t('aUdNotUpdated') })}</p>
          <p className="text-xs sm:text-sm text-gray-300 truncate">Email: {user.email || t('aUdNotUpdated')}</p>
        </div>
        <div className="rounded-xl border border-gray-700 bg-gray-800 p-4 sm:p-5">
          <p className="text-xs sm:text-sm text-gray-400">{t('aUdLabelAccountLevel')}</p>
          <p className="mt-1 flex items-center gap-2 text-lg sm:text-xl font-bold text-yellow-400"><Crown className="h-4 w-4 sm:h-5 sm:w-5" /> {user.vip || 'VIP 1'}</p>
          <p className="mt-2 text-xs sm:text-sm text-gray-300">{t('aUdTotalOrders', { count: user.total_orders || 0 })}</p>
          <p className="text-xs sm:text-sm text-gray-300">{t('aUdJoined', { value: new Date(user.created_at).toLocaleDateString('vi-VN') })}</p>
        </div>
        <div className="rounded-xl border border-gray-700 bg-gray-800 p-4 sm:p-5 sm:col-span-2 lg:col-span-1">
          <p className="text-xs sm:text-sm text-gray-400">{t('aUdLabelStatus')}</p>
          <div className={`mt-2 inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs sm:text-sm ${isActive ? 'bg-green-900/30 text-green-400' : 'bg-red-900/30 text-red-400'}`}>
            {isActive ? <CheckCircle className="h-3 w-3 sm:h-4 sm:w-4" /> : <Ban className="h-3 w-3 sm:h-4 sm:w-4" />}
            {isActive ? t('aUdStatusActive') : t('aUdStatusLocked')}
          </div>
          <p className="mt-3 text-xs sm:text-sm text-gray-300">{t('aUdDepositHistory', { count: user.deposit_count || 0 })}</p>
          <p className="text-xs sm:text-sm text-gray-300">{t('aUdWithdrawHistory', { count: user.withdraw_count || 0 })}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
        {cards.map((card) => {
          const Icon = card.icon;
          return <div key={card.label} className="rounded-xl border border-gray-700 bg-gray-800 p-4 sm:p-5">
            <div className="flex items-center justify-between"><p className="text-xs sm:text-sm text-gray-400">{card.label}</p><Icon className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400" /></div>
            <p className="mt-2 text-xl sm:text-2xl font-bold text-white break-words">{card.value}</p>
            {card.action && <button onClick={card.action} disabled={processing} className="mt-3 rounded-lg bg-gray-700 px-3 py-1.5 text-xs sm:text-sm font-medium text-white hover:bg-gray-600 disabled:cursor-not-allowed disabled:opacity-50">{t('aUdUnlock')}</button>}
          </div>;
        })}
      </div>

      <div className="mt-8 space-y-6">
        <HistoryTable title={t('aUdHistoryDeposits')} rows={histories.deposits} emptyText={t('aUdEmptyDeposits')} columns={[
          [t('aUdColAmount'), (row) => formatCurrency(row.amount)],
          [t('aUdColMethod'), (row) => row.payment_method || '—'],
          [t('aUdColTransactionCode'), (row) => row.transaction_code || '—'],
          [t('aUdColStatus'), (row) => row.status],
          [t('aUdColCreatedAt'), (row) => new Date(row.created_at).toLocaleString('vi-VN')]
        ]} />
        <HistoryTable title={t('aUdHistoryWithdraws')} rows={histories.withdraws} emptyText={t('aUdEmptyWithdraws')} columns={[
          [t('aUdColAmount'), (row) => formatCurrency(row.amount)],
          [t('aUdColBank'), (row) => `${row.bank_name || '—'}${row.account_number ? ` - ${row.account_number}` : ''}`],
          [t('aUdColAccountName'), (row) => row.account_name || '—'],
          [t('aUdColStatus'), (row) => row.status],
          [t('aUdColCreatedAt'), (row) => new Date(row.created_at).toLocaleString('vi-VN')]
        ]} />
        <HistoryTable title={t('aUdHistoryOrders')} rows={histories.orders} emptyText={t('aUdEmptyOrders')} columns={[
          [t('aUdColOrderCode'), (row) => `#${row.id}`],
          [t('aUdColProduct'), (row) => row.product_name || '—'],
          [t('aUdColPrice'), (row) => formatCurrency(row.reward)],
          [t('aUdColStatus'), (row) => row.status],
          [t('aUdColCreatedAt'), (row) => new Date(row.created_at).toLocaleString('vi-VN')]
        ]} />
      </div>

      {/* Referrer Modal */}
      {showReferrerModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
          <div className="w-full max-w-md rounded-2xl border border-gray-700 bg-gray-800 p-6 shadow-2xl">
            <div className="mb-5 flex items-start justify-between">
              <div>
                <h2 className="text-xl font-bold text-white">{t('aUdSetReferrer')}</h2>
                <p className="mt-1 text-sm text-gray-400">{t('aUdReferrerModalSubtitle')}</p>
              </div>
              <button type="button" onClick={() => setShowReferrerModal(false)} disabled={processing} className="rounded-lg p-1 text-gray-400 hover:bg-gray-700 hover:text-white">
                <X className="h-5 w-5" />
              </button>
            </div>

            <input
              type="text"
              value={referrerSearch}
              onChange={(e) => {
                setReferrerSearch(e.target.value);
                handleSearchReferrer(e.target.value);
              }}
              placeholder={t('aUdReferrerSearchPlaceholder')}
              className="w-full rounded-lg border border-gray-600 bg-gray-900 px-4 py-2 text-white placeholder-gray-400 focus:border-brand-500 focus:outline-none"
            />

            <div className="mt-4 max-h-96 overflow-y-auto space-y-2">
              {referrerResults.length === 0 ? (
                <p className="text-center text-gray-400 py-4">
                  {referrerSearch ? t('aUdReferrerNotFound') : t('aUdReferrerTypeToSearch')}
                </p>
              ) : (
                referrerResults.map((referrer) => (
                  <button
                    key={referrer.id}
                    onClick={() => handleSetReferrer(referrer.id)}
                    disabled={processing}
                    className="w-full flex items-center justify-between p-3 rounded-lg bg-gray-700 hover:bg-gray-600 transition-colors disabled:opacity-50"
                  >
                    <div className="text-left">
                      <p className="font-medium text-white">{referrer.username}</p>
                      <p className="text-xs text-gray-400">{referrer.email || referrer.phone}</p>
                    </div>
                    <UserPlus className="h-5 w-5 text-brand-400" />
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Referral Code Modal */}
      {showReferralCodeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
          <div className="w-full max-w-md rounded-2xl border border-gray-700 bg-gray-800 p-6 shadow-2xl">
            <div className="mb-5 flex items-start justify-between">
              <div>
                <h2 className="text-xl font-bold text-white">{t('aUdGenerateReferralCode')}</h2>
                <p className="mt-1 text-sm text-gray-400">{t('aUdCodeModalSubtitle')}</p>
              </div>
              <button type="button" onClick={() => setShowReferralCodeModal(false)} disabled={processing} className="rounded-lg p-1 text-gray-400 hover:bg-gray-700 hover:text-white">
                <X className="h-5 w-5" />
              </button>
            </div>

            <p className="text-gray-300 mb-6">
              {t('aUdCodeModalBody')}
            </p>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setShowReferralCodeModal(false)}
                disabled={processing}
                className="flex-1 rounded-lg bg-gray-700 px-4 py-2 font-medium text-white hover:bg-gray-600 disabled:opacity-50"
              >
                {t('aUdCancel')}
              </button>
              <button
                type="button"
                onClick={handleGenerateReferralCode}
                disabled={processing}
                className="flex-1 rounded-lg bg-brand-500 px-4 py-2 font-medium text-white hover:bg-brand-600 disabled:opacity-50"
              >
                {processing ? t('aUdCreatingCode') : <span className="flex items-center justify-center gap-2"><Dices className="h-4 w-4" /> {t('aUdGenerateCodeShort')}</span>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUserDetail;
